import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ordersApi, adminApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useLookups } from '../context/LookupContext';
import { fmtCurrency, statusClass, fileExtIcon } from '../utils/helpers';
import TopNav from '../components/layout/TopNav';

export default function OrderDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAdmin, user } = useAuth();
  const { lookups, lookupId, lookupName, activeLoanTypes } = useLookups();

  const [order, setOrder] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});
  const [borrowers, setBorrowers] = useState([]);
  const [payees, setPayees] = useState([]);
  const [notes, setNotes] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [noteInput, setNoteInput] = useState('');
  const [showAllNotes, setShowAllNotes] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [uploadName, setUploadName] = useState('');
  const [uploadFile, setUploadFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef();

  useEffect(() => {
    loadOrder();
    if (isAdmin) {
      adminApi.getUsers().then(u => setUsers(u.filter(x => x.isActive))).catch(console.error);
    }
  }, [id, isAdmin]);

  async function loadOrder() {
    setLoading(true);
    try {
      const data = await ordersApi.getById(id);
      setOrder(data);
      setForm({
        fileTypeId:    data.fileTypeId    || '',
        loanNumber:    data.loanNumber    || '',
        origSystemId:  data.origSystemId  || '',
        loanTypeId:    data.loanTypeId    || '',
        loanProgram:   data.loanProgram   || '',
        loanAmount:    data.loanAmount    || '',
        lender:        data.lender        || '',
        broker:        data.broker        || '',
        clientContact: data.clientContact || '',
        propertyType:  data.propertyType  || '',
        rate:          data.rate          || '',
        // Jumbo/MI come as bool from API — store as 'Yes'/'No'/'' for select
        jumbo:         data.jumbo === true ? 'Yes' : data.jumbo === false ? 'No' : '',
        mi:            data.mi    === true ? 'Yes' : data.mi    === false ? 'No' : '',
        lienStatusId:  data.lienStatusId  || '',
        purpose:       data.purpose       || '',
        street:        data.street        || '',
        city:          data.city          || '',
        state:         data.state         || '',
        zip:           data.zip           || '',
        statusId:      data.statusId      || '',
        assignedToId:  data.assignedToId  || '',
        closingDate:   data.closingDate   ? toInputDate(data.closingDate) : '',
        revenue:       data.revenue       || '',
        revReceived:   data.revReceived   || false,
        revDate:       data.revDate       ? toInputDate(data.revDate) : '',
      });
      setBorrowers(data.borrowers   || []);
      setPayees(data.payees         || []);
      setNotes(data.notes           || []);
      setAttachments(data.attachments || []);
    } catch (err) {
      console.error('Failed to load order', err);
    } finally {
      setLoading(false);
    }
  }

  function toInputDate(str) {
    if (!str) return '';
    const m = str.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (m) return `${m[3]}-${m[1]}-${m[2]}`;
    // Handle ISO datetime
    if (str.includes('T')) return str.split('T')[0];
    return str;
  }

  function set(field, val) { setForm(f => ({ ...f, [field]: val })); }

  // Derived: is this a Loan file type?
  const loanFileTypeId = lookupId('fileTypes', 'Loan');
  const isLoan = parseInt(form.fileTypeId) === loanFileTypeId;

  async function handleSave() {
    setSaving(true);
    try {
      await ordersApi.update(id, {
        fileTypeId:    parseInt(form.fileTypeId)   || null,
        loanNumber:    form.loanNumber             || null,
        origSystemId:  form.origSystemId ? parseInt(form.origSystemId) : null,
        loanTypeId:    form.loanTypeId   ? parseInt(form.loanTypeId)   : null,
        loanProgram:   form.loanProgram            || null,
        loanAmount:    form.loanAmount ? parseFloat(form.loanAmount)   : null,
        lender:        form.lender                 || null,
        broker:        form.broker                 || null,
        clientContact: form.clientContact          || null,
        propertyType:  form.propertyType           || null,
        rate:          isLoan ? (form.rate ? parseFloat(form.rate) : null) : null,
        jumbo:         isLoan ? (form.jumbo === 'Yes' ? true : form.jumbo === 'No' ? false : null) : null,
        mi:            isLoan ? (form.mi    === 'Yes' ? true : form.mi    === 'No' ? false : null) : null,
        lienStatusId:  isLoan ? (form.lienStatusId ? parseInt(form.lienStatusId) : null) : null,
        purpose:       isLoan ? (form.purpose || null) : null,
        street:        form.street  || null,
        city:          form.city    || null,
        state:         form.state   || null,
        zip:           form.zip     || null,
        statusId:      parseInt(form.statusId)     || null,
        assignedToId:  form.assignedToId ? parseInt(form.assignedToId) : null,
        closingDate:   form.closingDate || null,
        revenue:       form.revenue ? parseFloat(form.revenue) : null,
        revReceived:   form.revReceived,
        revDate:       form.revDate || null,
      });
      // Send id for existing rows (server updates in place) and omit it for
      // newly-added rows (server inserts). Rows removed in the UI are simply
      // absent from the list and the server deletes them.
      await ordersApi.replaceBorrowers(id, borrowers.map((b, i) => ({
        id:        b.id ?? null,
        sortOrder: i,
        firstName: b.firstName,
        lastName:  b.lastName,
        phone:     b.phone,
        email:     b.email,
      })));
      if (isAdmin) {
        const payeePayload = payees.map(p => ({
          id:       p.id ?? null,
          userId:   p.userId ? parseInt(p.userId) : null,
          amount:   parseFloat(p.amount) || 0,
          paid:     p.paid,
          // null, not '' — empty string fails DateTime? deserialisation
          paidDate: p.paidDate || null,
        }));
        await ordersApi.replacePayees(id, payeePayload);
      }
      const from = location.state?.from;
      const qs   = location.state?.queueState;
      if (from) navigate(from, { state: { queueState: qs }, replace: true });
      else navigate(-1);
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  async function handleAddNote() {
    if (!noteInput.trim()) return;
    try {
      const note = await ordersApi.addNote(id, noteInput.trim());
      setNotes(prev => [note, ...prev]);
      setNoteInput('');
    } catch (err) { alert('Failed to add note.'); }
  }

  async function handleUpload() {
    if (!uploadName.trim()) { alert('Please enter an attachment name.'); return; }
    if (!uploadFile) { alert('Please select a file.'); return; }
    try {
      const att = await ordersApi.uploadAttachment(id, uploadFile, uploadName.trim());
      setAttachments(prev => [att, ...prev]);
      setShowUpload(false); setUploadName(''); setUploadFile(null);
    } catch (err) { alert('Failed to upload attachment.'); }
  }

  async function handleDeleteAttachment(attId) {
    if (!window.confirm('Remove this attachment?')) return;
    try {
      await ordersApi.deleteAttachment(id, attId);
      setAttachments(prev => prev.filter(a => a.id !== attId));
    } catch (err) { alert('Failed to delete attachment.'); }
  }

  async function handleDownload(attId, fileName) {
    try {
      const blob = await ordersApi.downloadAttachment(id, attId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = fileName; a.click();
      URL.revokeObjectURL(url);
    } catch (err) { alert('Failed to download file.'); }
  }

  function addBorrower() {
    setBorrowers(prev => [...prev, { firstName: '', lastName: '', phone: '', email: '' }]);
  }
  function removeBorrower(i) {
    if (borrowers.length <= 1) return;
    setBorrowers(prev => prev.filter((_, idx) => idx !== i));
  }
  function updateBorrower(i, field, val) {
    setBorrowers(prev => prev.map((b, idx) => idx === i ? { ...b, [field]: val } : b));
  }

  function addPayee() {
    setPayees(prev => [...prev, { userId: '', payeeName: '', amount: 0, paid: false, paidDate: '' }]);
  }
  function removePayee(i) {
    setPayees(prev => prev.filter((_, idx) => idx !== i));
  }
  function updatePayeeField(i, field, val) {
    setPayees(prev => prev.map((p, idx) => {
      if (idx !== i) return p;
      const updated = { ...p, [field]: val };
      if (field === 'paid' && !val) updated.paidDate = '';
      // When userId changes, also update payeeName to match
      if (field === 'userId') {
        const u = users.find(x => x.id === parseInt(val));
        updated.payeeName = u?.fullName || '';
      }
      return updated;
    }));
  }

  if (loading) return <div><TopNav /><div className="page-content"><p>Loading...</p></div></div>;
  if (!order)  return <div><TopNav /><div className="page-content"><p>File not found.</p></div></div>;

  const payeeTotal = payees.reduce((s, p) => s + (parseFloat(p.amount) || 0), 0);
  const revenue    = parseFloat(form.revenue) || 0;
  const balance    = revenue - payeeTotal;
  const displayedNotes = showAllNotes ? notes : notes.slice(0, 10);

  // Display name helpers for header
  const statusDisplayName = lookupName('orderStatuses', order.statusId);

  return (
    <div>
      <TopNav />

      {/* Detail header */}
      <div style={{ background: '#fff', borderBottom: '1px solid #e0e4ec', padding: '1rem 2rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <button className="btn btn-sm" onClick={() => {
            const from = location.state?.from;
            const qs   = location.state?.queueState;
            if (from) navigate(from, { state: { queueState: qs }, replace: true });
            else navigate(-1);
          }}>← Back</button>
          <div>
            <div style={{ fontSize: 18, fontWeight: 700 }}>File #{order.fileNumber}</div>
            <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>
              {order.borrowers?.map(b => `${b.firstName} ${b.lastName}`).join(', ')} • {order.lender} • {order.loanNumber}
            </div>
          </div>
          <span className={`badge ${statusClass(statusDisplayName)}`}>{statusDisplayName}</span>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-sm" onClick={() => loadOrder()}>Discard</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save changes'}
          </button>
        </div>
      </div>

      <div className="page-content">

        {/* FILE INFORMATION */}
        <div className="card">
          <div className="section-hdr">File information</div>
          <div className="row5" style={{ marginBottom: 9 }}>
            <div className="fg"><label>File number</label><input readOnly value={order.fileNumber} /></div>
            <div className="fg"><label>File type *</label>
              <select value={form.fileTypeId} onChange={e => set('fileTypeId', e.target.value)}>
                <option value="">Select...</option>
                {lookups.fileTypes.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
            <div className="fg"><label>Date ordered</label><input readOnly value={order.dateOrdered} /></div>
            <div className="fg"><label>Status</label>
              <select value={form.statusId} onChange={e => set('statusId', e.target.value)}>
                {lookups.orderStatuses.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            <div className="fg"><label>Assigned to</label>
              {isAdmin ? (
                <select value={form.assignedToId} onChange={e => set('assignedToId', e.target.value)}>
                  <option value="">Unassigned</option>
                  {users.map(u => <option key={u.id} value={u.id}>{u.fullName}</option>)}
                </select>
              ) : (
                <>
                  <input
                    readOnly
                    value={parseInt(form.assignedToId) === user?.userId
                      ? user.fullName
                      : order.assignedToName || 'Unassigned'}
                  />
                  {parseInt(form.assignedToId) !== user?.userId && (
                    <button
                      type="button"
                      className="btn btn-sm"
                      style={{ marginTop: 5 }}
                      onClick={() => set('assignedToId', String(user.userId))}
                    >
                      Assign to me
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
          <div className="row5" style={{ marginBottom: 9 }}>
            <div className="fg"><label>Loan number *</label><input value={form.loanNumber} onChange={e => set('loanNumber', e.target.value)} /></div>
            <div className="fg"><label>Loan amount</label>
              <div style={{ position: 'relative' }}>
                <span style={{ position: 'absolute', left: 10, top: 8, color: '#888', fontSize: 13 }}>$</span>
                <input type="number" value={form.loanAmount} onChange={e => set('loanAmount', e.target.value)} style={{ paddingLeft: 22 }} />
              </div>
            </div>
            <div className="fg"><label>Loan type</label>
              <select value={form.loanTypeId} onChange={e => set('loanTypeId', e.target.value)}>
                <option value="">Select...</option>
                {activeLoanTypes.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
            <div className="fg"><label>Loan program</label><input value={form.loanProgram} onChange={e => set('loanProgram', e.target.value)} /></div>
            <div className="fg"><label>Property type</label><input value={form.propertyType} onChange={e => set('propertyType', e.target.value)} /></div>
          </div>
          <div className="row5">
            <div className="fg"><label>Lender *</label><input value={form.lender} onChange={e => set('lender', e.target.value)} /></div>
            <div className="fg"><label>Broker</label><input value={form.broker} onChange={e => set('broker', e.target.value)} /></div>
            <div className="fg"><label>Client contact</label><input value={form.clientContact} onChange={e => set('clientContact', e.target.value)} /></div>
            <div className="fg"><label>Orig system</label>
              <select value={form.origSystemId} onChange={e => set('origSystemId', e.target.value)}>
                <option value="">Select...</option>
                {lookups.origSystems.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            <div className="fg"><label>Closing date</label><input type="date" value={form.closingDate} onChange={e => set('closingDate', e.target.value)} /></div>
          </div>

          {/* Loan-only fields */}
          {isLoan && (
            <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid #eee' }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>Loan details</div>
              <div className="row5">
                <div className="fg"><label>Rate (%)</label><input value={form.rate} onChange={e => set('rate', e.target.value)} placeholder="e.g. 6.75" /></div>
                <div className="fg"><label>Jumbo</label>
                  <select value={form.jumbo} onChange={e => set('jumbo', e.target.value)}>
                    <option value="">Select...</option><option>Yes</option><option>No</option>
                  </select>
                </div>
                <div className="fg"><label>MI</label>
                  <select value={form.mi} onChange={e => set('mi', e.target.value)}>
                    <option value="">Select...</option><option>Yes</option><option>No</option>
                  </select>
                </div>
                <div className="fg"><label>Lien status</label>
                  <select value={form.lienStatusId} onChange={e => set('lienStatusId', e.target.value)}>
                    <option value="">Select...</option>
                    {lookups.lienStatuses.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </div>
                <div className="fg"><label>Purpose</label><input value={form.purpose} onChange={e => set('purpose', e.target.value)} placeholder="e.g. Purchase" /></div>
              </div>
            </div>
          )}
        </div>

        {/* TWO COLUMNS */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', alignItems: 'start' }}>

          {/* LEFT — Borrowers + Property */}
          <div>
            <div className="card">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                <div className="section-hdr" style={{ margin: 0, border: 'none', padding: 0 }}>Borrowers</div>
                <button className="btn btn-sm" onClick={addBorrower}>+ Add borrower</button>
              </div>
              {borrowers.map((b, i) => (
                <div key={i} className="borrower-row">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap', minWidth: 68 }}>Borrower {i + 1}</div>
                    <div style={{ flex: 1, minWidth: 110 }}><label style={{ marginBottom: 2 }}>First name *</label><input value={b.firstName} onChange={e => updateBorrower(i, 'firstName', e.target.value)} placeholder="Jane" /></div>
                    <div style={{ flex: 1, minWidth: 110 }}><label style={{ marginBottom: 2 }}>Last name *</label><input value={b.lastName} onChange={e => updateBorrower(i, 'lastName', e.target.value)} placeholder="Smith" /></div>
                    <div style={{ flex: 1, minWidth: 110 }}><label style={{ marginBottom: 2 }}>Phone</label><input type="tel" value={b.phone || ''} onChange={e => updateBorrower(i, 'phone', e.target.value)} placeholder="(555) 000-0000" /></div>
                    <div style={{ flex: 1.5, minWidth: 140 }}><label style={{ marginBottom: 2 }}>Email</label><input type="email" value={b.email || ''} onChange={e => updateBorrower(i, 'email', e.target.value)} placeholder="name@email.com" /></div>
                    {borrowers.length > 1 && (
                      <button className="btn-ghost" onClick={() => removeBorrower(i)} style={{ flexShrink: 0, marginTop: 14 }} title="Remove borrower"><TrashIcon /></button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="card" style={{ marginBottom: 0 }}>
              <div className="section-hdr">Property address</div>
              <div className="fg"><label>Street address</label><input value={form.street} onChange={e => set('street', e.target.value)} /></div>
              <div className="row3" style={{ marginTop: 8 }}>
                <div className="fg"><label>City</label><input value={form.city} onChange={e => set('city', e.target.value)} /></div>
                <div className="fg"><label>State</label><input value={form.state} onChange={e => set('state', e.target.value.toUpperCase())} maxLength={2} /></div>
                <div className="fg"><label>ZIP</label><input value={form.zip} onChange={e => set('zip', e.target.value)} /></div>
              </div>
            </div>
          </div>

          {/* RIGHT — Revenue + Payees */}
          <div>
            <div className="card">
              <div className="section-hdr">Revenue</div>
              <div className="fg"><label>Revenue</label>
                <div style={{ position: 'relative' }}>
                  <span style={{ position: 'absolute', left: 10, top: 8, color: '#888', fontSize: 13 }}>$</span>
                  <input type="number" value={form.revenue} onChange={e => set('revenue', e.target.value)} placeholder="0.00" style={{ paddingLeft: 22 }} />
                </div>
              </div>
              <div className="fg"><label>Revenue received</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
                  <input type="checkbox" checked={form.revReceived} onChange={e => set('revReceived', e.target.checked)} />
                  <input type="date" value={form.revDate} onChange={e => set('revDate', e.target.value)} disabled={!form.revReceived} />
                </div>
                <div style={{ fontSize: 11, color: '#aaa', marginTop: 4 }}>Check when received</div>
              </div>
            </div>

            {/* Payees — Admin only */}
            {isAdmin && (
              <div className="card" style={{ marginBottom: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                  <div className="section-hdr" style={{ margin: 0, border: 'none', padding: 0 }}>Payees</div>
                  <button className="btn btn-sm" onClick={addPayee}>+ Add payee</button>
                </div>
                <table className="payee-table">
                  <thead><tr><th>Payee</th><th>Amount</th><th>Paid date</th><th></th></tr></thead>
                  <tbody>
                    {payees.map((p, i) => (
                      <tr key={i}>
                        <td>
                          <select
                            value={p.userId || ''}
                            onChange={e => updatePayeeField(i, 'userId', e.target.value)}
                            style={{ fontSize: 12, padding: '4px 8px' }}
                          >
                            <option value="">Select...</option>
                            {users.map(u => <option key={u.id} value={u.id}>{u.fullName}</option>)}
                          </select>
                        </td>
                        <td>
                          <div style={{ position: 'relative' }}>
                            <span style={{ position: 'absolute', left: 6, top: 5, color: '#888', fontSize: 12 }}>$</span>
                            <input type="number" defaultValue={p.amount || ''} placeholder="0"
                              onBlur={e => updatePayeeField(i, 'amount', parseFloat(e.target.value) || 0)}
                              style={{ paddingLeft: 16, fontSize: 12, padding: '5px 8px 5px 16px' }} />
                          </div>
                        </td>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <input type="checkbox" checked={p.paid} onChange={e => updatePayeeField(i, 'paid', e.target.checked)} style={{ width: 'auto' }} />
                            <input type="date" value={p.paidDate ? toInputDate(p.paidDate) : ''} disabled={!p.paid}
                              onChange={e => updatePayeeField(i, 'paidDate', e.target.value)}
                              style={{ fontSize: 11, padding: '3px 6px' }} />
                          </div>
                        </td>
                        <td><button className="btn-ghost" onClick={() => removePayee(i)} title="Remove"><TrashIcon /></button></td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="payee-total-row">
                      <td style={{ fontSize: 12, color: '#888', padding: '7px 10px' }}>Total payees</td>
                      <td style={{ padding: '7px 10px' }}>{fmtCurrency(payeeTotal)}</td>
                      <td colSpan={2}></td>
                    </tr>
                    <tr>
                      <td style={{ fontSize: 12, color: '#888', padding: '4px 10px' }}>Revenue</td>
                      <td style={{ padding: '4px 10px', color: '#888' }}>{fmtCurrency(revenue)}</td>
                      <td colSpan={2}></td>
                    </tr>
                    <tr>
                      <td style={{ fontSize: 12, color: '#888', padding: '4px 10px 7px' }}>Balance</td>
                      <td style={{ padding: '4px 10px 7px', fontWeight: 600, color: balance < 0 ? '#991b1b' : balance > 0 ? '#166534' : '#888' }}>
                        {balance < 0 ? `(${fmtCurrency(Math.abs(balance))}) over` :
                         balance > 0 ? `${fmtCurrency(balance)} remaining` : '$0.00 balanced'}
                      </td>
                      <td colSpan={2}></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* NOTES */}
        <div className="card" style={{ marginTop: '1rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
            <div className="section-hdr" style={{ margin: 0, border: 'none', padding: 0 }}>Notes</div>
            <button className="btn btn-primary btn-sm" onClick={handleAddNote}>+ Add note</button>
          </div>
          <textarea value={noteInput} onChange={e => setNoteInput(e.target.value)} placeholder="Type a note and click Add note..." style={{ width: '100%', marginBottom: 14 }} />
          <table className="notes-table">
            <thead><tr><th style={{ width: 160 }}>User</th><th style={{ width: 180 }}>Date / time</th><th>Note</th></tr></thead>
            <tbody>
              {displayedNotes.length === 0 ? (
                <tr><td colSpan={3} style={{ padding: '1rem', color: '#aaa' }}>No notes yet.</td></tr>
              ) : displayedNotes.map(n => (
                <tr key={n.id}>
                  <td style={{ fontWeight: 500 }}>{n.authorName}</td>
                  <td style={{ color: '#888', fontSize: 12, whiteSpace: 'nowrap' }}>
                    {new Date(n.createdAt).toLocaleString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true })}
                  </td>
                  <td style={{ lineHeight: 1.5 }}>{n.body}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {notes.length > 10 && (
            <div style={{ textAlign: 'center', paddingTop: 12 }}>
              <button className="btn btn-sm" onClick={() => setShowAllNotes(p => !p)}>
                {showAllNotes ? 'Show recent 10' : `Show all ${notes.length} notes`}
              </button>
            </div>
          )}
        </div>

        {/* ATTACHMENTS */}
        <div className="card">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
            <div className="section-hdr" style={{ margin: 0, border: 'none', padding: 0 }}>Attachments</div>
            <button className="btn btn-primary btn-sm" onClick={() => setShowUpload(true)} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
              Add attachment
            </button>
          </div>
          {attachments.length === 0 ? (
            <p style={{ color: '#aaa', fontSize: 13 }}>No attachments yet.</p>
          ) : (
            <table className="att-table">
              <thead><tr><th style={{ width: 40 }}></th><th>Attachment name</th><th style={{ width: 180 }}>Date</th><th style={{ width: 160 }}>Uploaded by</th><th style={{ width: 120 }}></th></tr></thead>
              <tbody>
                {attachments.map(a => {
                  const ic = fileExtIcon(a.fileExtension);
                  return (
                    <tr key={a.id}>
                      <td><div className={`file-icon ${ic.cls}`}>{ic.lbl}</div></td>
                      <td>
                        <div style={{ fontWeight: 500 }}>{a.attachmentName}</div>
                        <div style={{ fontSize: 11, color: '#aaa', marginTop: 2 }}>{a.fileName}</div>
                      </td>
                      <td style={{ color: '#888', fontSize: 12 }}>
                        {new Date(a.uploadedAt).toLocaleString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true })}
                      </td>
                      <td style={{ color: '#888' }}>{a.uploadedBy}</td>
                      <td>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                          <button className="btn btn-sm" onClick={() => handleDownload(a.id, a.fileName)}>View</button>
                          <button className="btn-ghost" onClick={() => handleDeleteAttachment(a.id)} title="Delete"><TrashIcon /></button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Upload modal */}
      {showUpload && (
        <div className="modal-overlay" onClick={e => { if (e.target === e.currentTarget) setShowUpload(false); }}>
          <div className="modal-box">
            <div style={{ fontSize: 16, fontWeight: 600, marginBottom: '1.25rem' }}>Upload attachment</div>
            <div className="fg"><label>Attachment name *</label><input value={uploadName} onChange={e => setUploadName(e.target.value)} placeholder="e.g. Signed closing disclosure" /></div>
            <div className="fg"><label>File</label>
              <div
                className={`drop-zone${dragOver ? ' dragover' : ''}`}
                onClick={() => fileRef.current?.click()}
                onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={e => { e.preventDefault(); setDragOver(false); if (e.dataTransfer.files[0]) setUploadFile(e.dataTransfer.files[0]); }}
              >
                <input ref={fileRef} type="file" style={{ display: 'none' }} onChange={e => setUploadFile(e.target.files[0])} />
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#aaa" strokeWidth="1.5" style={{ margin: '0 auto 10px', display: 'block' }}>
                  <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
                </svg>
                <div style={{ fontSize: 13, color: '#888' }}>Click to browse or drag a file here</div>
                {uploadFile && <div style={{ fontSize: 12, color: '#185FA5', marginTop: 8, fontWeight: 500 }}>{uploadFile.name}</div>}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-primary" onClick={handleUpload}>Upload</button>
              <button className="btn" onClick={() => setShowUpload(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function TrashIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <polyline points="3 6 5 6 21 6"/>
      <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/>
      <path d="M10 11v6M14 11v6"/>
      <path d="M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2"/>
    </svg>
  );
}
